import { typedEntries } from '@/utils/typed-entries.ts'
import {
  Backdrop,
  CircularProgress,
  Table as MuiTable,
  Skeleton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import { styled } from '@mui/material/styles'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomTablePagination } from '@/components/CustomTablePagination/CustomTablePagination'

export type TableColumns<T> = {
  [K in keyof Partial<T>]: TableColumn<T, T[K]>
}

export interface PaginationModel {
  page: number
  pageSize: number
}

export interface SortModel<T> {
  field: keyof T
  direction: 'asc' | 'desc'
}

export interface TableColumn<T, V> {
  label: string
  sortable?: boolean
  align?: 'left' | 'center' | 'right'
  render?: (value: V, row: T) => ReactNode
  width?: string | number
}

export interface TableProps<T> {
  title?: string
  headless?: boolean
  columns: TableColumns<T>
  data: T[]
  sortModel?: SortModel<T>
  onSortModelChange?: (model: SortModel<T>) => void
  keyExtractor: (row: T, index: number) => string
  filters?: ReactNode
  actions?: ReactNode
  isLoading?: boolean
  paginationModel?: PaginationModel
  onPaginationModelChange?: (model: PaginationModel) => void
  totalItems?: number
  pageSizeOptions?: number[]
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.divider}`
    }
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      },
      '&:last-child .MuiTableCell-root': {
        borderBottom: 0
      }
    },
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      font: theme.typography.body2.fontSize
    }
  }
}))

export function Table<T>({
  columns,
  data,
  sortModel,
  onSortModelChange,
  keyExtractor,
  isLoading = false,
  paginationModel,
  onPaginationModelChange,
  totalItems,
  pageSizeOptions = [5, 10, 25, 50, 100]
}: Readonly<TableProps<T>>) {
  const { t } = useTranslation('_global')

  function loadingSkeleton() {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`} data-testid="skeleton-row">
        {typedEntries(columns).map(([column, props]) => (
          <TableCell
            key={`skeleton-${JSON.stringify(column)}`}
            align={props.align || 'left'}
          >
            <Skeleton
              variant="text"
              width="80%"
              height={24}
              data-testid="skeleton-cell"
            />
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function noDataRow() {
    return (
      <TableRow data-testid="no-data-row">
        <TableCell
          colSpan={Object.keys(columns).length}
          align="center"
          data-testid="no-data-cell"
          sx={{
            py: 4,
            color: 'text.secondary',
            fontStyle: 'italic'
          }}
        >
          {t('components.Table.noRowsLabel')}
        </TableCell>
      </TableRow>
    )
  }

  function dataRows() {
    return data.map((row, index) => (
      <TableRow
        key={keyExtractor(row, index)}
        data-testid="data-row"
        sx={{ opacity: isLoading ? 0.5 : 1 }}
      >
        {typedEntries(columns).map(([column, props]) => (
          <TableCell
            key={JSON.stringify(column)}
            align={props.align || 'left'}
            data-testid={`cell-${String(column)}`}
          >
            {props.render ? props.render(row[column], row) : String(row[column] || '')}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <div>
      <StyledTableContainer
        sx={{ height: data.length === 0 ? 'auto' : paginationModel ? 'auto' : 400 }}
      >
        <MuiTable>
          <TableHead>
            <TableRow>
              {typedEntries(columns).map(([column, props]) => (
                <TableCell
                  key={JSON.stringify(column)}
                  align={props.align || 'left'}
                  style={{ width: props.width }}
                  sortDirection={
                    sortModel?.field === column ? sortModel.direction : false
                  }
                >
                  {props.sortable ? (
                    <TableSortLabel
                      IconComponent={FilterListIcon}
                      active={sortModel?.field === column}
                      direction={sortModel?.direction || 'asc'}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          scale: '1 -1'
                        }
                      }}
                      onClick={() => {
                        if (onSortModelChange) {
                          const newSort =
                            sortModel?.field === column && sortModel?.direction === 'asc'
                              ? 'desc'
                              : 'asc'
                          onSortModelChange({ field: column, direction: newSort })
                        }
                      }}
                    >
                      <Typography variant={'hDataTable'}>{props.label}</Typography>
                    </TableSortLabel>
                  ) : (
                    <Typography variant={'hDataTable'}>{props.label}</Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            data-testid="table-body"
            sx={{
              opacity: isLoading && data.length > 0 ? 0.5 : 1,
              transition: 'opacity 0.2s'
            }}
          >
            {data.length === 0
              ? isLoading
                ? loadingSkeleton()
                : noDataRow()
              : dataRows()}
          </TableBody>
        </MuiTable>
        <Backdrop
          sx={theme => ({
            position: 'absolute',
            background:
              'linear-gradient(60deg,rgba(59, 33, 74, 0.25) 0%, rgba(87, 139, 199, 0.25) 50%, rgba(59, 49, 0, 0.15) 100%)',
            zIndex: theme.zIndex.drawer + 1
          })}
          open={!!data.length && isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </StyledTableContainer>
      {paginationModel && onPaginationModelChange && (
        <CustomTablePagination
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          totalItems={totalItems || data.length}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </div>
  )
}
